import React, { useState, useCallback, useEffect } from 'react';
import { Sheet } from './ui/sheet';
import { useMeetingStore } from '../stores/meetingStore';
import { useToast } from './ui/toast';
import { liveKitService } from '../services/livekit';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';

import { Button } from './ui/button';
import { cn } from '../lib/utils';
import {
    Mic,
    Volume2,

    Sparkles,
    Monitor,
    Signal,
    Wifi,
    Users,
    Smartphone,
} from 'lucide-react';

interface MeetingSettingsProps {
    open: boolean;
    onClose: () => void;
}

// Detect if device is mobile (has multiple cameras)
const isMobileDevice = (): boolean => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
};

const MeetingSettings: React.FC<MeetingSettingsProps> = ({ open, onClose }) => {
    const { addToast } = useToast();

    // Get all settings from the store
    const audioInputDevices = useMeetingStore((state) => state.audioInputDevices);
    const audioOutputDevices = useMeetingStore((state) => state.audioOutputDevices);
    const selectedAudioInput = useMeetingStore((state) => state.selectedAudioInput);
    const selectedAudioOutput = useMeetingStore((state) => state.selectedAudioOutput);
    const videoQuality = useMeetingStore((state) => state.videoQuality);
    const facingMode = useMeetingStore((state) => state.facingMode);

    const isTestingSound = useMeetingStore((state) => state.isTestingSound);

    // Get store actions
    const setAudioInputDevices = useMeetingStore((state) => state.setAudioInputDevices);
    const setAudioOutputDevices = useMeetingStore((state) => state.setAudioOutputDevices);
    const setSelectedAudioInput = useMeetingStore((state) => state.setSelectedAudioInput);
    const setSelectedAudioOutput = useMeetingStore((state) => state.setSelectedAudioOutput);
    const setVideoQuality = useMeetingStore((state) => state.setVideoQuality);
    const setFacingMode = useMeetingStore((state) => state.setFacingMode);

    const setIsTestingSound = useMeetingStore((state) => state.setIsTestingSound);

    const [activeTab, setActiveTab] = useState<'audio' | 'video'>('video');
    const [isApplyingSettings, setIsApplyingSettings] = useState(false);
    const [isMobile] = useState(isMobileDevice());

    // Enumerate available devices
    const enumerateDevices = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();

            const audioInputs = devices
                .filter((device) => device.kind === 'audioinput' && device.deviceId)
                .map((device) => ({
                    deviceId: device.deviceId,
                    label: device.label || `Microphone ${device.deviceId.substring(0, 5)}`,
                    kind: device.kind,
                }));

            const audioOutputs = devices
                .filter((device) => device.kind === 'audiooutput' && device.deviceId)
                .map((device) => ({
                    deviceId: device.deviceId,
                    label: device.label || `Speaker ${device.deviceId.substring(0, 5)}`,
                    kind: device.kind,
                }));

            setAudioInputDevices(audioInputs);
            setAudioOutputDevices(audioOutputs);

            // Set default devices if not already selected
            if (!selectedAudioInput && audioInputs.length > 0) {
                setSelectedAudioInput(audioInputs[0].deviceId);
            }
            if (!selectedAudioOutput && audioOutputs.length > 0) {
                setSelectedAudioOutput(audioOutputs[0].deviceId);
            }
        } catch (error) {
            console.error('Error enumerating devices:', error);
        }
    }, [
        selectedAudioInput,
        selectedAudioOutput,
        setAudioInputDevices,
        setAudioOutputDevices,
        setSelectedAudioInput,
        setSelectedAudioOutput,
    ]);

    // Enumerate devices when sheet opens and listen for device changes
    useEffect(() => {
        if (open) {
            enumerateDevices();

            // Listen for device changes
            navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);

            return () => {
                navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
            };
        }
    }, [open, enumerateDevices]);

    // Test speaker functionality
    const testSpeaker = useCallback(async () => {
        try {
            setIsTestingSound(true);

            // Create a short test tone using AudioContext
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            // Create a MediaStreamDestination to capture the audio
            const destination = audioContext.createMediaStreamDestination();

            oscillator.connect(gainNode);
            gainNode.connect(destination);

            oscillator.frequency.value = 440; // A4 note
            oscillator.type = 'sine';

            // Fade in
            gainNode.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);

            // Fade out
            gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);

            // Create an audio element to play through the selected output device
            const audio = new Audio();
            audio.srcObject = destination.stream;

            // Set the output device using setSinkId
            if (selectedAudioOutput && 'setSinkId' in audio) {
                try {
                    await (audio as any).setSinkId(selectedAudioOutput);
                } catch (e) {
                    console.warn('setSinkId not supported or failed:', e);
                }
            }

            // Play the audio
            await audio.play();

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);

            setTimeout(() => {
                setIsTestingSound(false);
                audio.pause();
                audio.srcObject = null;
                audioContext.close();
            }, 600);
        } catch (error) {
            console.error('Error testing speaker:', error);
            setIsTestingSound(false);
            addToast({
                title: 'Speaker test failed',
                description: 'Could not play test sound',
                variant: 'destructive',
            });
        }
    }, [selectedAudioOutput, setIsTestingSound, addToast]);

    // Handle video quality change
    const handleVideoQualityChange = useCallback(async (newQuality: string) => {
        setVideoQuality(newQuality);

        // Apply to LiveKit if connected
        if (liveKitService.isConnected()) {
            setIsApplyingSettings(true);
            try {
                await liveKitService.updateVideoConstraints(newQuality, facingMode);
            } catch (error) {
                console.error('Failed to update video quality:', error);
                addToast({
                    title: 'Failed to update video quality',
                    description: 'Please try again',
                    variant: 'destructive',
                });
            } finally {
                setIsApplyingSettings(false);
            }
        }
    }, [facingMode, setVideoQuality, addToast]);

    // Handle facing mode change
    const handleFacingModeChange = useCallback(async (newMode: 'user' | 'environment') => {
        setFacingMode(newMode);

        // Apply to LiveKit if connected
        if (liveKitService.isConnected()) {
            setIsApplyingSettings(true);
            try {
                await liveKitService.updateVideoConstraints(videoQuality, newMode);
                // addToast({
                //     title: 'Camera mode updated',
                //     description: `Switched to ${newMode === 'user' ? 'front' : 'back'} camera`,
                //     variant: 'success',
                // });
            } catch (error) {
                console.error('Failed to update camera mode:', error);
                // addToast({
                //     title: 'Failed to update camera mode',
                //     description: 'Please try again',
                //     variant: 'destructive',
                // });
            } finally {
                setIsApplyingSettings(false);
            }
        }
    }, [videoQuality, setFacingMode, addToast]);

    // Handle audio input change
    const handleAudioInputChange = useCallback(async (deviceId: string) => {
        setSelectedAudioInput(deviceId);

        // Apply to LiveKit if connected
        if (liveKitService.isConnected()) {
            try {
                await liveKitService.updateAudioInputDevice(deviceId);
            } catch (error) {
                console.error('Failed to update audio input device:', error);
                addToast({
                    title: 'Failed to update microphone',
                    description: 'Please try again',
                    variant: 'destructive',
                });
            }
        }
    }, [setSelectedAudioInput, addToast]);

    // Handle audio output change
    const handleAudioOutputChange = useCallback(async (deviceId: string) => {
        setSelectedAudioOutput(deviceId);

        // Apply to LiveKit if connected
        if (liveKitService.isConnected()) {
            try {
                await liveKitService.updateAudioOutputDevice(deviceId);
            } catch (error) {
                console.error('Failed to update audio output device:', error);
                addToast({
                    title: 'Failed to update speaker',
                    description: 'Please try again',
                    variant: 'destructive',
                });
            }
        }
    }, [setSelectedAudioOutput, addToast]);

    return (
        <Sheet open={open} onClose={onClose} title="Meeting Settings">
            {/* Tabs Navigation */}
            <div className="flex border-b border-border bg-muted/30">
                <button
                    onClick={() => setActiveTab('audio')}
                    className={cn(
                        'flex-1 px-6 py-3 text-sm font-medium transition-colors relative',
                        activeTab === 'audio'
                            ? 'text-primary bg-card'
                            : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    Audio
                    {activeTab === 'audio' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('video')}
                    className={cn(
                        'flex-1 px-6 py-3 text-sm font-medium transition-colors relative',
                        activeTab === 'video'
                            ? 'text-primary bg-card'
                            : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    Video
                    {activeTab === 'video' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                    )}
                </button>
            </div>

            {/* Tab Content */}
            <div className="p-6 space-y-6">
                {/* Audio Tab */}
                {activeTab === 'audio' && (
                    <div className="space-y-6 animate-in fade-in duration-200">
                        {/* Microphone Dropdown */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">
                                Microphone
                            </label>
                            <Select
                                value={selectedAudioInput}
                                onValueChange={handleAudioInputChange}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select microphone" />
                                </SelectTrigger>
                                <SelectContent>
                                    {audioInputDevices.map((device) => (
                                        <SelectItem key={device.deviceId} value={device.deviceId}>
                                            <div className="flex items-center gap-2">
                                                <Mic className="w-4 h-4" />
                                                {device.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                    {audioInputDevices.length === 0 && (
                                        <div className="p-2 text-sm text-muted-foreground text-center">
                                            No microphones found
                                        </div>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Speaker Dropdown with Test Button */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-foreground">
                                    Speaker
                                </label>
                                {isTestingSound && (
                                    <span className="text-xs text-primary animate-pulse">
                                        Playing test sound...
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Select
                                    value={selectedAudioOutput}
                                    onValueChange={handleAudioOutputChange}
                                >
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Select speaker" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {audioOutputDevices.map((device) => (
                                            <SelectItem key={device.deviceId} value={device.deviceId}>
                                                <div className="flex items-center gap-2">
                                                    <Volume2 className="w-4 h-4" />
                                                    {device.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                        {audioOutputDevices.length === 0 && (
                                            <div className="p-2 text-sm text-muted-foreground text-center">
                                                No speakers found
                                            </div>
                                        )}
                                    </SelectContent>
                                </Select>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={testSpeaker}
                                    disabled={isTestingSound}
                                    title="Test Speaker"
                                    className="shrink-0"
                                >
                                    <Volume2 className={cn('h-4 w-4', isTestingSound && 'text-primary animate-pulse')} />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Video Tab */}
                {activeTab === 'video' && (
                    <div className="space-y-6 animate-in fade-in duration-200">
                        {/* Quality Dropdown */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">
                                Video Quality
                            </label>
                            <Select
                                value={videoQuality}
                                onValueChange={handleVideoQualityChange}
                                disabled={isApplyingSettings}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select quality" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="auto">
                                        <div className="flex items-center gap-2">
                                            <Sparkles className="w-4 h-4" />
                                            Auto (Recommended)
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="1080p">
                                        <div className="flex items-center gap-2">
                                            <Monitor className="w-4 h-4" />
                                            Full HD (1080p)
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="720p">
                                        <div className="flex items-center gap-2">
                                            <Monitor className="w-4 h-4" />
                                            High Definition (720p)
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="360p">
                                        <div className="flex items-center gap-2">
                                            <Signal className="w-4 h-4" />
                                            Standard Definition (360p)
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="240p">
                                        <div className="flex items-center gap-2">
                                            <Wifi className="w-4 h-4" />
                                            Low Bandwidth (240p)
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Camera Mode Dropdown - Only show on mobile devices */}
                        {isMobile && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">
                                    Camera Mode
                                </label>
                                <Select
                                    value={facingMode}
                                    onValueChange={(value: 'user' | 'environment') => handleFacingModeChange(value)}
                                    disabled={isApplyingSettings}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select mode" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="user">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-4 h-4" />
                                                Front Camera
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="environment">
                                            <div className="flex items-center gap-2">
                                                <Smartphone className="w-4 h-4" />
                                                Back Camera
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}


                    </div>
                )}
            </div>
        </Sheet>
    );
};

export default MeetingSettings;
